import React, { useEffect, useState, useRef } from 'react';
import DialogContent from '@material-ui/core/DialogContent';
import CoachingKangaroo from '../../../api/CoachingKangaroo';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import ConversationItem from '../ConversationItem/ConversationItem';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import GetAppIcon from '@material-ui/icons/GetApp';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { withStyles } from '@material-ui/core/styles';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import ConversationStatusButtons from './ConversationStatusButtons/ConversationStatusButtons';
import ConversationFeelingButtons from './ConversationFeelingButtons/ConversationFeelingButtons';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFrown, faMeh, faSmile } from '@fortawesome/free-regular-svg-icons';
import yellow from '@material-ui/core/colors/yellow';
import green from '@material-ui/core/colors/green';
import red from '@material-ui/core/colors/red';
import {truncateWithTooltip} from "../../../utils/textTruncate";
import {TraceDetailTabs} from '../ConversationItem/TracePanelShared';

const formatDate = (value) => {
  if (value) {
    return new Intl.DateTimeFormat('cs-CZ', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(Date.parse(value));
  } else {
    return '';
  }
};

const styles = (theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  }
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const ConversationDetail = (props) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const [data, setData] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const { t } = useTranslation();
  const [detail, setDetail] = useState('LLM');
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadingRecording, setLoadingRecording] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const audioRef = useRef(null);
  const previousRecordingUrlRef = useRef(null);

  useEffect(() => {
    setDetail('LLM');
  }, [props.conversationPreview.id]);

  useEffect(() => {
    setDataFetched(false);

    CoachingKangaroo.get(
      '/api/training/v1/' +
        encodeURIComponent(props.projectId) +
        '/conversation/' +
        props.conversationPreview.id
    )
      .then((response) => {
        setErrorMessage(null);
        setData(response.data);
        setDataFetched(true);
      })
      .catch((error) => {
        if (error.message) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('Unknown error');
        }
        setData(null);
        setDataFetched(true);
      });
  }, [props.conversationPreview, props.projectId]);

  const stopAudioPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
      setIsPlaying(false);
    }
  };

  const cleanupRecordingUrl = (url) => {
    if (url) {
      window.URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    stopAudioPlayback();
    cleanupRecordingUrl(previousRecordingUrlRef.current);
    previousRecordingUrlRef.current = null;
    
    setHasRecording(false);
    setRecordingUrl(null);
    
    if (!props.conversationPreview?.id || !props.projectId) {
      return;
    }

    CoachingKangaroo.get(
      '/api/training/v1/' +
        encodeURIComponent(props.projectId) +
        '/conversation/' +
        props.conversationPreview.id +
        '/recording',
      {
        responseType: 'blob'
      }
    )
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        previousRecordingUrlRef.current = url;
        setRecordingUrl(url);
        setHasRecording(true);
      })
      .catch((error) => {
        if (error.response?.status === 404) {
          setHasRecording(false);
          setRecordingUrl(null);
        } else {
          console.error('Error checking recording:', error);
          setHasRecording(false);
          setRecordingUrl(null);
        }
      });
  }, [props.conversationPreview?.id, props.projectId]);

  const handlePlayRecording = async () => {
    if (isPlaying && audioRef.current) {
      stopAudioPlayback();
      return;
    }

    if (!recordingUrl) {
      return;
    }

    setLoadingRecording(true);
    try {
      const audio = new Audio(recordingUrl);
      audioRef.current = audio;
      
      audio.addEventListener('ended', () => {
        audioRef.current = null;
        setIsPlaying(false);
      });

      audio.addEventListener('error', () => {
        audioRef.current = null;
        setIsPlaying(false);
        setErrorMessage(t('Failed to play recording'));
      });

      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      setErrorMessage(error.message || t('Failed to play recording'));
      setIsPlaying(false);
    } finally {
      setLoadingRecording(false);
    }
  };

  const handleDownloadRecording = () => {
    if (!recordingUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = recordingUrl;
    link.setAttribute('download', `recording-${props.conversationPreview.id}.mp3`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  useEffect(() => {
    return () => {
      stopAudioPlayback();
      cleanupRecordingUrl(recordingUrl);
    };
  }, [recordingUrl]);

  return (
    <React.Fragment>
      {!dataFetched ? (
        <React.Fragment>
          <DialogTitle
            id="customized-dialog-title"
            onClose={props.handleDialogClose}
          >
            {t('Conversation Detail')}: ...
          </DialogTitle>
          <DialogContent className='flex flex-col'>
            <CircularProgress className='flex m-auto' />
          </DialogContent>
        </React.Fragment>
      ) : errorMessage ? (
        <React.Fragment>
          <DialogTitle
            id="customized-dialog-title"
            onClose={props.handleDialogClose}
          >
            {`${t('Conversation Detail')}: `}
            {truncateWithTooltip(props.conversationPreview.text)}
          </DialogTitle>
          <DialogContent>
            <Grid
              container
              direction="row"
              justify="space-between"
              alignItems="flex-start"
            >
              <Grid item>
                <Grid container spacing={2}>
                  <Grid item>
                    {t('Platform')}: {props.conversationPreview.platform}
                  </Grid>
                  <Grid item>
                    {t('Session ID')}: {props.conversationPreview.id}
                  </Grid>
                  <Grid item>
                    {t('Date')}:{' '}
                    {formatDate(props.conversationPreview['started-at'])}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider style={{ marginTop: '1em', marginBottom: '1em' }} />
            <Grid container>
              <Alert severity="error">Error: {errorMessage}</Alert>
            </Grid>
          </DialogContent>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <DialogTitle
            id="customized-dialog-title"
            onClose={props.handleDialogClose}
            style={{ paddingBottom: '8px' }}
          >
            {`${t('Conversation Detail')}: `}
            {props.conversationPreview.summaryAI
              ? truncateWithTooltip(props.conversationPreview.summaryAI)
              : truncateWithTooltip(props.conversationPreview.text)}
          </DialogTitle>
          <div className="conversation-detail-header" style={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 10,
            padding: '4px 24px 8px 24px',
            borderBottom: '1px solid #e0e0e0'
          }}>
            <Grid
              container
              direction="row"
              justify="space-between"
              alignItems="flex-start"
              spacing={1}
            >
              <Grid item>
                <Grid container direction="column" spacing={1}>
                  <Grid item>
                    <Grid container spacing={2} direction="row" justify="flex-start">
                      <Grid item>
                        {t('Platform')}: {props.conversationPreview.platform}
                      </Grid>
                      <Grid item>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {t('Session ID')}: {props.conversationPreview.id}
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                                navigator.clipboard.writeText(props.conversationPreview.id).catch(() => {});
                              }
                            }}
                            style={{ padding: 2 }}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Grid>
                      <Grid item>
                        {t('Date')}:{' '}
                        {formatDate(props.conversationPreview['started-at'])}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Tooltip
                        title={!hasRecording ? t("No recording found") : ""}
                        arrow
                      >
                        <span>
                          <Button
                            variant="outlined"
                            onClick={handlePlayRecording}
                            disabled={loadingRecording || !hasRecording}
                            startIcon={
                              loadingRecording ? (
                                <CircularProgress size={16} />
                              ) : isPlaying ? (
                                <StopIcon />
                              ) : (
                                <PlayArrowIcon />
                              )
                            }
                            color="default"
                          >
                            {loadingRecording
                              ? t("Načítání...")
                              : isPlaying
                                ? t("Zastavit nahrávku")
                                : t("Přehrát nahrávku")}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={!hasRecording ? t("No recording found") : t("Stáhnout nahrávku")}
                        arrow
                      >
                        <span>
                          <IconButton
                            onClick={handleDownloadRecording}
                            disabled={!hasRecording}
                            color="default"
                            size="small"
                            style={{
                              border: '1px solid rgba(0, 0, 0, 0.23)',
                              borderRadius: '4px'
                            }}
                          >
                            <GetAppIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <TraceDetailTabs
                        tabs={[
                          { id: 'LLM', label: t('Detail LLM') },
                          { id: 'NLP', label: t('Detail NLP') },
                        ]}
                        activeTab={detail}
                        onTabChange={setDetail}
                      />
                    </div>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item>
                <Grid container direction="column" alignItems="flex-end" spacing={1}>
                  <Grid item>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button
                        variant="outlined"
                        onClick={props.onShowPrevious}
                        disabled={!props.hasPrevious}
                      >
                        {t('Previous')}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={props.onShowNext}
                        disabled={!props.hasNext}
                      >
                        {t('Next')}
                      </Button>
                    </div>
                  </Grid>
                  <Grid item>
                    <Grid container spacing={1} direction="row" justify="flex-end">
                      <Grid item>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {t('Feeling')}
                          </Typography>
                          <ConversationFeelingButtons
                            project={props.projectId}
                            setDirtyConversation={props.setDirtyConversation}
                            conversationId={props.conversationPreview.id}
                            feeling={props.conversationPreview.feeling}
                            feelingAI={props.conversationPreview.feelingAI}
                            showLabels={true}
                          />
                        </div>
                      </Grid>
                      {props.conversationPreview.feelingAI && (
                        <Grid item>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {t('AI Feeling')}
                            </Typography>
                            <Grid container>
                              <Grid item>
                                <FontAwesomeIcon
                                  style={{
                                    fontSize: '2em',
                                    marginLeft: '30px',
                                    marginRight: '30px',
                                    color:
                                      props.conversationPreview.feelingAI === 'NONE'
                                        ? yellow[700]
                                        : props.conversationPreview.feelingAI === 'GOOD'
                                          ? green[700]
                                          : red[700]
                                  }}
                                  icon={
                                    props.conversationPreview.feelingAI === 'NONE'
                                      ? faMeh
                                      : props.conversationPreview.feelingAI === 'GOOD'
                                        ? faSmile
                                        : faFrown
                                  }
                                />
                              </Grid>
                            </Grid>
                          </div>
                        </Grid>
                      )}
                      <Grid item>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {t('Conversation Status')}
                          </Typography>
                          <ConversationStatusButtons
                            project={props.projectId}
                            setDirtyConversation={props.setDirtyConversation}
                            conversationId={props.conversationPreview.id}
                            status={props.conversationPreview.status}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>
          <DialogContent style={{ paddingTop: 0 }}>
            {data.map((conversationItem) => {
              return (
                <React.Fragment key={conversationItem.id}>
                  <Divider style={{ marginTop: '1em', marginBottom: '1em' }} />
                  <ConversationItem
                    project={props.projectId}
                    conversationId={props.conversationPreview.id}
                    inputType={conversationItem.inputType}
                    data={conversationItem}
                    detail={detail}
                  />
                </React.Fragment>
              );
            })}
          </DialogContent>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default ConversationDetail;
